/**
 * pdfTextExtractor.ts — Extraction de texte brut depuis un PDF
 * Version simplifiée pour l'indexation RAG des documents réglementaires.
 * Utilise PDF.js (déjà configuré dans le projet).
 */

import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

/**
 * Extrait le texte brut de toutes les pages d'un fichier PDF.
 * Retourne le texte complet et le texte par page (pour le chunking avec numéros de page).
 */
export async function extractFullText(file: File): Promise<{ text: string; pageTexts: string[] }> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pageTexts: string[] = [];

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const textContent = await page.getTextContent();

    // Reconstruire le texte ligne par ligne en respectant les positions Y
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const items = (textContent.items as any[])
      .filter(item => typeof item.str === 'string' && item.str.trim() !== '')
      .map(item => ({
        text: item.str as string,
        y: Math.round(item.transform[5] as number), // Position Y arrondie pour grouper les lignes
      }));

    // Grouper par ligne (même Y à ±3px)
    const lines: Map<number, string[]> = new Map();
    for (const item of items) {
      const existingKey = [...lines.keys()].find(k => Math.abs(k - item.y) <= 3);
      const key = existingKey ?? item.y;
      if (!lines.has(key)) lines.set(key, []);
      lines.get(key)!.push(item.text);
    }

    // Trier les lignes du haut vers le bas (Y décroissant dans PDF)
    const sortedLines = [...lines.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([, words]) => words.join(' '));

    pageTexts.push(sortedLines.join('\n'));
  }

  return {
    text: pageTexts.join('\n\n--- PAGE ---\n\n'),
    pageTexts,
  };
}
